import _ from "lodash";
import { DateTime } from "luxon";
import { DAYS_OF_WEEK, MONTHS_OF_YEAR } from "../../constants";
import { circularArrayStep, getDaysInMonth, makeDblDigit, monthNameOrNum } from "../helpers";

const monthList = MONTHS_OF_YEAR.map(_.lowerCase);

const crossesMidnight = (start, end) => start.day !== end.day;
const crossesMonth = (start, end) => start.month !== end.month;
const startDateChanged = (converted, original) => converted !== original;
const getHour = (time) => time.split(":")[0];
const getMinute = (time) => time.split(":")[1];
const getOffsetDirection = (original, converted) => {
  if (converted.offset === original.offset) return 0;

  return converted.offset < original.offset ? -1 : 1;
};

const makeDate = (dateObj) => {
  const { year, month, day } = dateObj;
  return `${year}-${makeDblDigit(month)}-${makeDblDigit(day)}`;
};
const makeTime = (timeObj) => {
  const { hour, minute } = timeObj;
  return `${makeDblDigit(hour)}:${makeDblDigit(minute)}`;
};
const makeDateObjBase = (rule) => {
  const year = new Date().getFullYear();
  const month = rule.months?.length ? monthNameOrNum(rule.months[0]) : 1;
  const day = rule.month_days?.length ? makeDblDigit(rule.month_days[0]) : 1;
  return { year, month, day };
};
const makeStartEndInstances = (rule, timezone) => {
  const { startHr, startMin, endHr, endMin } = parseTimes(rule);
  const dateObjBase = makeDateObjBase(rule);

  const startObj = { ...dateObjBase, hour: startHr, minute: startMin };
  const endObj = { ...dateObjBase, hour: endHr, minute: endMin };

  const start = DateTime.fromObject(startObj, { zone: timezone });
  const end = DateTime.fromObject(endObj, { zone: timezone });
  return { start, end };
};
const makeConvertedObjs = (start, end, zone) => {
  const newStart = start.setZone(zone).toObject();
  const newEnd = end.setZone(zone).toObject();
  return {
    newStart,
    newEnd,
  };
};
const parseTimes = (rule) => {
  const startHr = getHour(rule.startTime);
  const startMin = getMinute(rule.startTime);
  const endHr = getHour(rule.endTime);
  const endMin = getMinute(rule.endTime);
  return { startHr, startMin, endHr, endMin };
};
const shiftWeekdays = (rule, offsetDir) => {
  let weekDays = [...rule.week_days];
  const allDays = DAYS_OF_WEEK.map((d) => d.toLowerCase());

  if (weekDays.length !== 7 && !weekDays.includes("all")) {
    weekDays = weekDays.map((d) => circularArrayStep(allDays, allDays.indexOf(d), offsetDir));
  }
  return { weekDays };
};
const shiftMonthDays = (rule, offsetDir) => {
  let monthDays = [...rule.month_days];
  let months = [...rule.months];

  if (offsetDir !== 0 && monthDays.length) {
    monthDays = monthDays.flatMap((d) =>
      rule.months.map((m) => {
        if (
          (getDaysInMonth(_.lowerCase(m)) > d && d > 1) ||
          (d === 1 && offsetDir === 1) ||
          (d === getDaysInMonth(_.lowerCase(m)) && offsetDir === -1)
        ) {
          return d + offsetDir;
        }

        // easy cases have been satisfied in above if statement, meaning at this point,
        // we know that (d === 1 && offsetDir === -1) || (d === getDaysInMonth(_.lowerCase(m)) && offsetDir === 1)
        if (months.length) {
          months = months.map((mo) =>
            circularArrayStep(monthList, monthList.indexOf(mo), offsetDir),
          );
        }

        return offsetDir === -1 ? getDaysInMonth(_.lowerCase(months[0])) : 1;
      }),
    );
  }

  return { monthDays, months };
};
export const ruleExpander = (rules) => {
  const naiveRules = rules.flatMap((rule) => {
    let expandedRules = [];
    if (rule.month_days?.length) {
      const moreRules = rule.month_days.flatMap((d) =>
        rule.months.map((m) => ({ ...rule, week_days: [], month_days: [d], months: [m] })),
      );
      expandedRules = [...expandedRules, ...moreRules];
    }
    if (rule.week_days?.length) {
      expandedRules = [...expandedRules, { ...rule, month_days: [] }];
    }
    return expandedRules;
  });
  return naiveRules;
};

/**
 * @param {Object} schedule an object containing an array of rules.
 * @param {String} tzFrom timezone from.
 * @param {String} tzTo timezone to.
 * @returns {Object} the schedule object containing rules adjusted for the timezone conversion.
 */
export default (schedule, tzFrom = "UTC", tzTo = "UTC") => {
  if (tzFrom === tzTo) return schedule;
  let rules = ruleExpander(schedule.rules);

  rules = rules.flatMap((r) => {
    const { start, end } = makeStartEndInstances(r, tzFrom);
    const { newStart, newEnd } = makeConvertedObjs(start, end, tzTo);

    const newStartInstance = DateTime.fromObject(newStart, { zone: tzTo });

    const offsetDirection = getOffsetDirection(start, newStartInstance);
    const { weekDays: shiftedWeekDays } = shiftWeekdays(r, offsetDirection);
    const { monthDays: shiftedMonthDays, months: shiftedMonths } = shiftMonthDays(
      r,
      offsetDirection,
    );

    const differentStartDate = startDateChanged(makeDate(newStart), makeDate(start.toObject()));
    const crossMidnight = crossesMidnight(newStart, newEnd);
    const crossMonth = crossesMonth(newStart, newEnd);

    const singleRule = {
      ...r,
      startTime: makeTime(newStart),
      endTime: makeTime(newEnd),
    };

    if (crossMidnight) {
      let ruleA = { ...singleRule, endTime: "23:59" };
      let ruleB = { ...singleRule, startTime: "00:00" };
      if (crossMonth && !_.isEmpty(r.month_days)) {
        if (offsetDirection === 1) {
          ruleB = { ...ruleB, months: shiftedMonths };
        } else if (offsetDirection === -1) {
          ruleA = { ...ruleA, months: shiftedMonths };
        }
      }
      if (differentStartDate) {
        ruleA = { ...ruleA, week_days: shiftedWeekDays, month_days: shiftedMonthDays };
      } else {
        ruleB = { ...ruleB, week_days: shiftedWeekDays, month_days: shiftedMonthDays };
      }
      return [ruleA, ruleB];
    }

    if (!differentStartDate) return singleRule;

    return {
      ...singleRule,
      week_days: shiftedWeekDays,
      month_days: shiftedMonthDays,
      months: shiftedMonths,
    };
  });

  return { ...schedule, rules };
};
